// Generative engine: plans bars into timed events (pure, seeded) and schedules them on the AudioContext timeline.
// Note times are always trackStart + offset (never accumulated); advance(until) schedules everything before `until`.
import { clamp } from '../engine/core.js';
import { hold, createRegistry } from './dsp.js';
import { QUALITIES, spell, voice, bassNote, liftKey } from './harmony.js';
import { barHits, swing, COMPS } from './rhythm.js';
import { phrase, improv } from './melody.js';
import { planTrack, stream, normVibe, bandLayers, HANDOFF_BARS } from './arrange.js';
import { createEP, createBass, createKit, createLead, createPerc, fadeOut } from './instruments.js';
import { MIX } from './mixer.js';
import { styleOf } from './styles/index.js';

export const TRACK_GAIN = 0.5;          // per-track dry/wet level into the music bus (tuning knob)
const VINYL_BOOST = 1.585;              // +4 dB during intros and gaps
// A band change's level (the solo keys' lift, bandLayers) moves the track bus LEVEL_LEAD before the bar line it changes on,
// fast (τ LEVEL_TAU): settled 5 τ later, ahead of the bar's earliest note (a downbeat's human timing can fall ~25 ms
// before its bar line). The band coming back never lands on the lifted bus, and the solo keys start at their level.
export const LEVEL_LEAD = 0.065, LEVEL_TAU = 0.008;
const mod12 = n => ((n % 12) + 12) % 12;

/* ================= bar planning (pure: same plan + same call order → same events) ================= */
export function createBarState(plan) {
  const map = [], keysLevel = styleOf(plan).keysLevel;
  plan.sections.forEach((s, si) => {
    const L = bandLayers(s.layers, plan.vibe.band, keysLevel), beats = progOf(plan, s.prog).beats;
    const lift = plan.lift && si >= plan.lift.si ? plan.lift.by : 0;
    for (let j = 0; j < s.bars; j++) {
      const pass = s.passes ? s.passes[Math.min(s.passes.length - 1, Math.floor((j * 4) / beats))] : s.prog;
      map.push({ si, sec: s.name, j, n: s.bars, prog: s.prog, pass, lift, L, prev: plan.sections[si - 1]?.name || null });
    }
  });
  return {
    map, rH: stream(plan.seed, 11), rR: stream(plan.seed, 12), rM: stream(plan.seed, 13), rU: stream(plan.seed, 16),
    rS: stream(plan.seed, 17),                     // the style's own bar-level draws (jazzhop never touches it)
    prevVoicing: null, prevBass: 40, pushed: -1, nextComp: null, phrases: new Map(), statement: 0, lastLead: null, lifted: null,
  };
}

/** A section's progression by key: 'A' · 'B' · 'C' (the bridge) · a variant pass ('A1'…'A3', 'B1', 'B2'); a variant a
 *  style dropped gives its own loop back, 'final' and unknown keys progA. */
export const progOf = (plan, k) => plan.vars?.[k] || (k === 'C' && plan.progC) || (k && k[0] === 'B' ? plan.progB : plan.progA);

/** The plan as bar i hears it: past a key lift (§7) the same plan in the lifted key, so every part (a style's hooks too)
 *  voices, walks and sings in it. */
export function planAt(plan, st, i) {
  const by = st.map[Math.min(i, st.map.length - 1)].lift;
  if (!by) return plan;
  if (!st.lifted || st.lifted.base !== plan || st.lifted.by !== by) st.lifted = { base: plan, by, plan: { ...plan, key: liftKey(plan.key, by) } };
  return st.lifted.plan;
}

/** Chords sounding in bar i: [{ root, q, start (beat 0..3), beats, onset }] */
export function barChords(plan, st, i) {
  const b = st.map[Math.min(i, st.map.length - 1)];
  if (b.prog === 'final') {
    if (b.j < 2) return chordsIn(plan.progA, b.j);
    const q = plan.key.mode === 'minor' ? 'm9' : plan.seed % 2 ? 'maj9' : '69';
    return [{ root: 0, q, start: 0, beats: 4, onset: b.j === 2, final: true }];
  }
  return chordsIn(progOf(plan, b.pass ?? b.prog), b.j);
}
function chordsIn(prog, j) {
  const b0 = (j * 4) % prog.beats, out = [];
  for (const c of prog.chords) {
    const s = Math.max(c.start, b0), e = Math.min(c.start + c.beats, b0 + 4);
    if (e > s) out.push({ root: c.root, q: c.q, start: s - b0, beats: e - s, onset: c.start >= b0 });
  }
  return out;
}
const chordPcs = (plan, c) => QUALITIES[c.q].map(iv => mod12(plan.key.tonic + c.root + iv));
const sameChord = (a, b) => a && b && a.root === b.root && a.q === b.q;
/** the keys' voicing of chord `c` after `prev` (a style's own, else harmony.js voice()), as planBar makes it */
const voicingOf = (plan, c, prev) => {
  const S = styleOf(plan), sp = spell(plan.key, c);
  return S.voicing ? S.voicing(sp, prev, c, plan) : voice(sp, prev, plan.register);
};
/**
 * What sounds `pos` 16th steps after a track's first bar line (before it: its first chord; past its end: its last), for
 * a note to be picked over it (jam.js): { plan: the plan as that bar hears it, key: its key (past a lift, the lifted
 * one), chord, root: its root's pitch class, tones: the pitch classes sounding, bar, step (0–15) }. The chord is the
 * bar's own at that step, or the next bar's once the keys have anticipated it (planBar's push on step 14). The tones
 * are the root (the bass's) and the keys' voicing of the chord, which a style may fill with other notes than the
 * quality names (a triad for a 9th chord, a Picardy third, a pedal note). planBar keeps every strike of a bar in its
 * `keys`, [step, chord, voicing, pushed]: the voicing is the last strike of the chord in the bar; else one ringing in
 * from the bar before (a push, whose downbeat strike is left out; the outro's final chord, never struck again); else
 * the bar's next strike of it; else, in a bar that isn't planned yet, the one its first strike will make.
 */
export function harmonyAt(plan, st, pos) {
  const s = Math.floor(pos + 1e-6), bar = clamp(Math.floor(s / 16), 0, st.map.length - 1), step = clamp(s - bar * 16, 0, 15);
  const b = st.map[bar], P = planAt(plan, st, bar), chords = barChords(P, st, bar), here = b.keys || [];
  const last = bar && st.map[bar - 1].lift === b.lift ? st.map[bar - 1].keys || [] : [];   // (a lift moves every note)
  const push = here.find(k => k[3] && k[0] <= step);
  const chord = push ? push[1] : chords.find(c => step >= c.start * 4 && step < (c.start + c.beats) * 4) || chords[chords.length - 1];
  const is = k => sameChord(k[1], chord) && !k[1].final === !chord.final;
  const hit = here.filter(k => k[0] <= step && is(k)).pop() || last.filter(k => is(k) && (k[3] || chord.final)).pop() || here.find(is);
  const root = mod12(P.key.tonic + chord.root), v = hit ? hit[2] : voicingOf(P, chord, st.prevVoicing);
  return { plan: P, key: P.key, chord, root, tones: [...new Set([root, ...v.map(mod12)])].sort((x, y) => x - y), bar, step };
}
/** a comping cell id, weighted (jazzhop: hold · charleston · pulse; a style: its own cells) */
const pickComp = (w, r) => {
  const ids = Object.keys(w);
  let u = r() * ids.reduce((s, id) => s + w[id], 0);
  for (const id of ids) if ((u -= w[id]) < 0) return id;
  return ids[ids.length - 1];
};
/** the voicing's notes a comping cell picks (indices; negative from the top) */
const pickNotes = (v, pick) => pick.map(k => v[k < 0 ? v.length + k : k]).filter(m => m != null);
const SIG = { kick: 0.003, snare: 0.004, hat: 0.006, rim: 0.004 };                  // drum timing σ (others 5 ms)
const LAGGED = new Set(['snare', 'rim', 'clap']);                                    // laid back by plan.snareLag

/** Turn bar i into timed events (seconds from track start). Mutates only st (its streams and leading state). */
export function planBar(plan, i, st) {
  const S = styleOf(plan), base = plan;
  plan = planAt(base, st, i);
  const b = st.map[i], L = b.L, sd = 60 / plan.bpm / 4, bar0 = i * 16 * sd, barDur = 16 * sd;
  const ev = [];
  const gauss = () => (st.rU() + st.rU() + st.rU() - 1.5) * 2;                 // ≈ N(0, 1)
  const at = (s, sigma, lag = 0) => Math.max(0, bar0 + s * sd + swing(s, sd, plan.swing) + lag + gauss() * sigma);
  const vh = v => clamp(v * (1 + (st.rU() * 2 - 1) * 0.08), 0.05, 1);
  const chords = barChords(plan, st, i);
  const chordAt = s => chords.find(c => s >= c.start * 4 && s < (c.start + c.beats) * 4) || chords[chords.length - 1];
  const nextChords = i + 1 < st.map.length ? barChords(plan, st, i + 1) : null;

  /* ---- mixer automation (tone LP, vinyl, wow, dust) and the track level (a band change mid-track) ---- */
  const fx = (type, v, tau = 1, dt = 0) => ev.push({ t: bar0 + dt, k: 'fx', fx: type, v, tau, bar: i });
  if (i === 0) { fx('dust', plan.dust); fx('wow', 1); fx('vinyl', VINYL_BOOST, 0.5); fx('tone', 900, 0.02); }
  else if (L.level !== st.map[i - 1].L.level) fx('level', L.level, LEVEL_TAU);     // heard LEVEL_LEAD before the bar line
  const drift = () => plan.tone * (1 + (st.rH() * 2 - 1) * 0.12);
  // intro opens from 900 Hz to the track's tone (the bar-0 glide starts just after the snap to 900 Hz)
  if (b.sec === 'intro') fx('tone', 900 * Math.pow(plan.tone / 900, (b.j + 1) / b.n), barDur / 3, i === 0 ? 0.12 : 0);
  else if (b.j === 0) {
    if (b.sec === 'break') fx('tone', 1800, 0.4);
    else if (b.sec !== 'outro') fx('tone', drift(), b.prev === 'break' ? 0.8 : 0.3);
    if (b.prev === 'intro') fx('vinyl', 1, 2);
  } else if (b.j % 4 === 0 && (b.sec === 'A' || b.sec === 'B')) fx('tone', drift(), barDur);
  if (b.j % 2 === 0) fx('wow', 0.6 + st.rH() * 0.8);
  if (L.dipLast && b.j === b.n - 1) fx('tone', 500, 0.25);
  if (b.sec === 'outro' && b.j === b.n - 2) { fx('tone', 700, barDur * 0.6); fx('vinyl', VINYL_BOOST, 1.5); }

  /* ---- EP comping ---- */
  const hits = [];                                          // [step, lengthSteps, chord, velScale, relTau, pick?, plan?]
  const vel = plan.ep.vel;
  if (L.ep === 'intro' || L.ep === 'whole' || L.ep === 'soft') {
    for (const c of chords) hits.push([c.start * 4, c.beats * 4, c, L.ep === 'intro' ? 0.85 : L.ep === 'soft' ? 0.5 : 0.8, 0.12]);
  } else if (L.ep === 'cold') hits.push([0, 12, chords[0], 0.85, 0.1]);       // a cold start's chord, the pickup after it
  else if (L.ep === 'outro') {
    if (b.j < 2) for (const c of chords) hits.push([c.start * 4, c.beats * 4, c, 0.9, 0.1]);
    else if (b.j === 2) hits.push([0, (b.n - 2) * 16, chords[0], 0.85, 0.9]);        // final tonic, release τ 0.9 s
  } else if (S.comps) {
    // a style's cells over every chord of the bar, cut at its end; a chord no cell starts in gets one held hit. After a
    // push only the downbeat goes: the anticipated chord rings on to the bar's next cell (its cell was drawn with the push).
    const cells = S.comps[st.nextComp ?? pickComp(plan.ep.comp, st.rH)], pushed = st.pushed === i;
    st.nextComp = null;
    if (pushed) st.pushed = -1;
    for (const c of chords) {
      const c0 = c.start * 4, c1 = c0 + c.beats * 4, own = cells.filter(([s]) => s >= c0 && s < c1 && !(pushed && s === 0));
      if (!own.length && !(pushed && c0 === 0)) hits.push([c0, c1 - c0, c, 1, 0.08]);
      for (const [s, len, v = 1, pick = null] of own) hits.push([s, Math.min(len, c1 - s), c, v, 0.08, pick]);
    }
  } else if (chords.length > 1 || st.pushed === i) {
    for (const c of chords) hits.push([c.start * 4, c.beats * 4, c, 1, 0.08]);
  } else {
    const cells = COMPS[pickComp(plan.ep.comp, st.rH)];
    cells.forEach(([s, len], k) => hits.push([s, k === cells.length - 1 ? 16 - s : len, chords[0], k ? 0.9 : 1, 0.08]));
  }
  if (st.pushed === i) { hits.shift(); st.pushed = -1; }    // this bar's first chord was anticipated on step 14
  // push: anticipate the next bar's chord on step 14 (p .25), voiced on that bar's plan (into a lift, in the lifted key)
  const last = chords[chords.length - 1];
  if (L.ep === 'comp' && nextChords && st.map[i + 1].L.ep === 'comp' && !sameChord(last, nextChords[0]) && st.rH() < plan.ep.push) {
    const h = hits[hits.length - 1];
    if (h && h[0] < 14) {
      for (const x of hits) x[1] = Math.min(x[1], 14 - x[0]);          // nothing rings on under the pushed chord
      let len = 2 + nextChords[0].beats * 4;
      if (S.comps) {                                                    // held until the next bar's first cell after its downbeat
        st.nextComp = pickComp(plan.ep.comp, st.rH);
        const next = S.comps[st.nextComp].find(([s]) => s > 0 && s < nextChords[0].beats * 4);
        if (next) len = 2 + next[0];
      }
      hits.push([14, len, nextChords[0], 0.95, 0.08, null, planAt(base, st, i + 1)]);
      st.pushed = i + 1;
    }
  }
  for (const [s, len, c, vs, rel, pick, P = plan] of hits) {
    const sp = spell(P.key, c), v = S.voicing ? S.voicing(sp, st.prevVoicing, c, P) : voice(sp, st.prevVoicing, P.register);
    st.prevVoicing = v;
    (b.keys ||= []).push([s, c, v, !!nextChords && c === nextChords[0]]);   // what the keys sound from this step on (harmonyAt)
    const notes = pick ? pickNotes(v, pick) : v;
    if (notes.length) ev.push({ t: at(s, 0.004), k: 'ep', notes, vel: vh(vel * vs), dur: Math.max(0.1, len * sd - 0.03), strum: plan.ep.strum, rel, bar: i });
  }

  /* ---- drums (a silent kit still plans its groove: the bass follows it and the random streams stay in step) ---- */
  const dh = barHits(plan, { sec: b.sec, j: b.j, n: b.n, drums: L.drums, hatsFirst: L.hatsFirst }, st.rR, S);
  for (const h of dh.hits) {
    const lag = LAGGED.has(h.v) ? plan.snareLag : 0;
    const e = { t: at(h.s, SIG[h.v] ?? 0.005, lag), k: h.v, vel: vh(h.vel), open: !!h.open, ghost: !!h.ghost, bar: i };
    if (L.kit) ev.push(e);
  }

  /* ---- bass: follows the kick, root on chord changes, approach notes (a style can write its own line) ---- */
  // (under 'keys' the bass and the lead are planned all the same, just not played, like the silent kit)
  const bm = L.bass, heard = e => { if (!L.solo) ev.push(e); };
  const bassOn = bm === 'kick' || bm === 'whole' || (bm === 'last' && b.j === b.n - 1) || (bm === 'outro' && b.j < 3);
  const line = bassOn && S.bass ? S.bass({
    plan, st, i, b, mode: bm, chords, nextChords, chordAt, kicks: dh.hits.filter(h => h.v === 'kick').map(h => h.s), stop: dh.stop, r: st.rR,
  }) : null;
  if (line) {
    for (const n of line) heard({ t: at(n.s, 0.004, 0.003), k: 'bass', midi: n.midi, vel: vh(n.vel ?? 0.82), dur: Math.max(0.25, n.dur) * sd, slide: !!n.slide, bar: i });
  } else if (bassOn) {
    // the track's line (jazzhop's; a style's own bass hook keeps the kick line for the bars it leaves to this one): on
    // the kicks, with the 5th or octave on more of them ('fifth'), a walk in quarters ('walk'), or a fill ('melodic')
    const line = bm === 'kick' && !S.bass ? plan.bass.line || 'kick' : 'kick', walk = line === 'walk';
    const kicks = bm === 'kick' ? dh.hits.filter(h => h.v === 'kick').map(h => h.s) : [];
    const changes = chords.map(c => c.start * 4), into = walk ? 12 : 14;
    let steps = [...new Set([...(walk ? [0, 4, 8, 12] : kicks), ...changes])].sort((a, c) => a - c);
    let approach = false;
    if (bm === 'kick' && nextChords && nextChords[0].root !== last.root && st.rR() < (walk ? 0.8 : 0.5)) { steps = steps.filter(s => s < into); steps.push(into); approach = true; }
    if (line === 'melodic' && !steps.some(s => s >= 9 && s <= 11) && st.rR() < 0.5) steps = [...steps, 10].sort((a, c) => a - c);
    if (dh.stop != null) steps = steps.filter(s => s < dh.stop);
    const endStep = dh.stop != null ? dh.stop : 16;
    const tone = (c, root, ivs) => {                 // a chord tone above the root (the first of `ivs` the chord has)
      const q = QUALITIES[c.q] || [0, 4, 7], iv = ivs.find(x => q.some(y => mod12(y) === x)) ?? 7;
      return root + iv <= 52 ? root + iv : root + iv - 12;
    };
    steps.forEach((s, k) => {
      const c = chordAt(s), rootPc = mod12(plan.key.tonic + c.root);
      const root = bassNote(rootPc, st.prevBass);
      let m = root;
      if (approach && s === into) {
        const target = bassNote(mod12(plan.key.tonic + nextChords[0].root), root);
        m = st.rR() < 0.6 ? target - 1 : target + 1;
      } else if (walk && !changes.includes(s)) {
        m = s === 4 ? tone(c, root, st.rR() < 0.6 ? [3, 4, 7] : [7]) : s === 8 ? (st.rR() < 0.6 ? tone(c, root, [7]) : root + 12 <= 52 ? root + 12 : root)
          : tone(c, root, [10, 11, 7]);
      } else if (line === 'melodic' && s === 10) m = tone(c, root, st.rR() < 0.5 ? [10, 11, 9] : [7, 3, 4]);
      else if (!changes.includes(s) && s % 8 !== 0 && st.rR() < (line === 'fifth' ? 0.6 : 0.25)) {
        m = st.rR() < 0.6 ? (root + 7 <= 50 ? root + 7 : root - 5) : (root + 12 <= 52 ? root + 12 : root);
      }
      const nextS = k + 1 < steps.length ? steps[k + 1] : endStep;
      const dur = Math.max(1, nextS - s) * sd * plan.bass.factor;
      heard({ t: at(s, 0.004, 0.003), k: 'bass', midi: m, vel: vh(s === 0 ? 0.92 : 0.82), dur, slide: st.rR() < 0.15, bar: i });
      st.prevBass = root;
    });
  }

  /* ---- lead: motif phrases (4 bars: motif 2 · bar 3 · answer 1), developed; a solo's lines; or a style's own line,
     asked for on every bar ---- */
  const own = plan.lead && S.lead ? S.lead({ plan, st, i, b, chords }) : null;
  if (own) {
    for (const n of own) heard({ t: at(n.s, 0.008), k: 'lead', midi: n.midi, vel: vh(n.vel), dur: n.dur * sd * 0.95, glide: !!n.glide, bar: i });
  } else if (plan.lead && L.lead) {
    const g = Math.floor(b.j / 4), id = `${b.si}:${g}`;
    if (!st.phrases.has(id)) {
      const startBar = i - (b.j % 4);
      const pcsAt = s => { const cs = barChords(plan, st, startBar + Math.floor(s / 16)), beat = (s % 16) / 4;
        return chordPcs(plan, cs.find(c => beat >= c.start && beat < c.start + c.beats) || cs[0]); };
      let notes;
      if (L.lead === 'solo') {
        notes = improv(pcsAt, plan.key, st.rM, { prevLast: st.lastLead, register: plan.register, unit: plan.bpm >= 110 ? 4 : 2, busy: plan.lead.busy ?? 0.55 });
      } else {
        // B and the bridge sing the second motif (the bridge upside down); a section's first phrase states its motif as
        // written, later ones are sequenced, displaced or fragmented, and bar 3 rests, echoes or runs on
        const rest = g % 2 === 1 && st.rM() < 0.2, u = st.rM(), w = st.rM(), f = st.rM();
        const treat = g === 0 || u < 0.3 ? 'state' : u < 0.55 ? 'seq' : u < 0.72 ? 'displace' : u < 0.87 ? 'frag' : 'state';
        const by = treat === 'seq' ? (w < 0.5 ? 1 : -1) * (w < 0.2 || w > 0.8 ? 2 : 1) : treat === 'displace' ? (w < 0.6 ? 2 : 4) : 0;
        const second = b.sec === 'B' && plan.lead.motifB;
        notes = rest ? [] : phrase(second ? plan.lead.motifB : plan.lead.motif, pcsAt, plan.key, st.rM, {
          statement: ++st.statement, inv: b.prog === 'C', treat, by, form: f < 0.45 ? 'rest' : f < 0.75 ? 'echo' : 'run',
          prevLast: st.lastLead, register: plan.register,
        });
      }
      if (notes.length) st.lastLead = notes[notes.length - 1].midi;
      st.phrases.set(id, notes);
    }
    const notes = st.phrases.get(id), bj = b.j % 4;
    notes.forEach((n, k) => {
      if (Math.floor(n.s / 16) !== bj) return;
      const p = notes[k - 1], glide = !!p && n.s - (p.s + p.d) <= 1;
      heard({ t: at(n.s % 16, 0.008), k: 'lead', midi: n.midi, vel: vh(n.v), dur: n.d * sd * 0.95, glide, bar: i });
    });
  }
  return ev.sort((a, c) => a.t - c.t);
}

/* ================= scheduling ================= */
const wowSeconds = cents => (Math.pow(2, cents / 1200) - 1) / (2 * Math.PI * 0.556);

/**
 * createEngine(ac, mixer, { scene, vibe, onTrack, lite, registry, log, trace }) →
 *   { start(seed, t), next(seed?), tune(plan, t0), follow(fn), nudge(d), advance(until), key(), queue, setScene(scene),
 *     setVibe(vibe), stats(), dispose() }
 * onTrack(plan) fires when a track actually starts (checked on every advance()).
 * queue = the next QUEUE plans, each chained from the one before (nextSeed, related key); queue[0] plays next.
 * follow(fn) keeps time for the radio (§13): fn(plan) → { plan, start } names the track after `plan` and when it starts
 * (ac time), in place of the queue and the handoff gap, and late notes are dropped instead of shifting the track.
 * trace(event, time) hears every event as it is scheduled, and a chord struck again on tuning in with `again` (tests).
 */
export function createEngine(ac, mixer, { scene = null, vibe = null, onTrack = () => {}, lite = false, registry = createRegistry(), log = null, trace = null } = {}) {
  const LOOK = 0.1, cap = lite ? 10 : 16, QUEUE = 5;
  let cur = null, queue = [], pending = [], jobs = [], curScene = scene, curVibe = normVibe(vibe), follow = null;
  let reanchors = 0, trackN = 0, disposed = false, lastSweep = 0, skips = 0;
  const played = { ep: 0, bass: 0, kick: 0, snare: 0, hat: 0, rim: 0, lead: 0 };

  // the next track follows on: a key related to the one this ended in, fresh loops, and (on Mix) its style's run
  const planAfter = plan => planTrack(plan.nextSeed, {
    scene: curScene, prevKey: plan.endKey || plan.key, recent: plan.recent, mix: plan.mix || { left: 0, recent: [plan.vibe.style] }, vibe: curVibe,
  });
  function refill() {
    if (cur) while (queue.length < QUEUE) queue.push(planAfter(queue.length ? queue[queue.length - 1] : cur.plan));
  }

  function build(plan, t0) {
    const tag = `trk${++trackN}`, sd = 60 / plan.bpm / 4, tStart = Math.max(ac.currentTime, t0 - 0.05), st = createBarState(plan);
    const dry = ac.createGain(), wet = ac.createGain();
    dry.gain.value = wet.gain.value = TRACK_GAIN * st.map[0].L.level;
    dry.connect(mixer.musicIn); wet.connect(mixer.revIn);
    const busVoice = registry.add({ src: [], all: [dry, wet], tag: `${tag}:bus` });
    const opts = { registry, tag, t0: tStart };
    const S = styleOf(plan), own = S.voices ? S.voices(ac, dry, wet, plan, { ...opts, cap, stepDur: sd, r: stream(plan.seed, 27), log }) : {};
    return {
      plan, tag, start: t0, sd, barDur: 16 * sd, st, q: [], qi: 0, nextBar: 0,
      dry, wet, busVoice, dead: false, notified: false,
      ep: own.ep || createEP(ac, dry, wet, plan.ep, { ...opts, cap, r: stream(plan.seed, 26), log }),
      bass: own.bass || createBass(ac, dry, opts),
      kit: own.kit || createKit(ac, dry, wet, plan.drums.kit, opts),
      lead: plan.lead ? own.lead || createLead(ac, dry, wet, plan.lead, { ...opts, stepDur: sd }) : null,
      perc: null,                                    // clap, shaker, tom, block: built on the first hit (styles only)
    };
  }
  /** the voice for a drum event: the kit's own, else the percussion (built on first use) */
  function drum(track, k) {
    if (track.kit[k]) return track.kit;
    if (!track.perc) track.perc = createPerc(ac, track.dry, track.wet, track.plan.drums.perc, { registry, tag: track.tag });
    return track.perc[k] ? track.perc : null;
  }
  /** stop a track's persistent voices at `at` and disconnect its buses just after */
  function retire(track, at) {
    track.q = []; track.qi = 0;
    for (const inst of [track.ep, track.bass, track.kit, track.lead, track.perc]) if (inst) inst.dispose(at);
    const bus = track.busVoice;
    bus.end = at + 0.2;
    jobs.push({ at: at + 0.2, fn: () => registry.free(bus) });
  }
  function runJobs(now) {
    if (!jobs.length) return;
    const due = jobs.filter(j => now >= j.at);
    if (!due.length) return;
    jobs = jobs.filter(j => now < j.at);
    for (const j of due) j.fn();
  }
  function planNext(track) {
    const i = track.nextBar++;
    const evs = planBar(track.plan, i, track.st);
    const q = track.q, lastT = q.length > track.qi ? q[q.length - 1].t : -Infinity;
    for (const e of evs) q.push(e);
    if (evs.length && evs[0].t < lastT) {
      const rest = q.slice(track.qi).sort((a, c) => a.t - c.t);
      track.q = rest; track.qi = 0;
    }
  }
  function dispatch(track, e) {
    const T = track.start + e.t;
    if (e.k !== 'fx') played[e.k] = (played[e.k] || 0) + 1;
    if (trace) trace(e, T);
    switch (e.k) {
      case 'ep': track.ep.chord(T, e.notes, e.vel, e.dur, e.strum, e.rel); break;
      case 'bass': track.bass.note(T, e.midi, e.vel, e.dur, e.slide); break;
      case 'kick': track.kit.kick(T, e.vel, e); break;
      case 'snare': track.kit.snare(T, e.vel, e.ghost, e); break;
      case 'hat': track.kit.hat(T, e.vel, e.open, e); break;
      case 'rim': track.kit.rim(T, e.vel, e); break;
      case 'lead': if (track.lead) track.lead.note(T, e.midi, e.vel, e.dur, e.glide); break;
      case 'fx': {
        const t = Math.max(T, ac.currentTime);
        if (e.fx === 'tone') mixer.tone.frequency.setTargetAtTime(e.v, t, e.tau);
        else if (e.fx === 'vinyl') mixer.vinyl.gain.setTargetAtTime(e.v, t, e.tau);
        else if (e.fx === 'dust') mixer.vinyl.crackle.setTargetAtTime(MIX.CRACKLE * e.v, t, 2);
        else if (e.fx === 'wow') mixer.tape.wow.setTargetAtTime(wowSeconds(track.plan.wowCents * e.v), t, 1.5);
        else if (e.fx === 'level') {
          const early = Math.max(T - LEVEL_LEAD, ac.currentTime);
          for (const g of [track.dry.gain, track.wet.gain]) g.setTargetAtTime(TRACK_GAIN * e.v, early, e.tau);
        }
        break;
      }
      default: { const d = drum(track, e.k); if (d) d[e.k](T, e.vel, e); }      // clap, shaker, tom, block
    }
  }
  /** The main thread froze: shift trackStart so the next downbeat lands at now + 0.1 s (missed notes are dropped). */
  function reanchor(now, bar) {
    reanchors++;
    cur.q = cur.q.slice(cur.qi).filter(e => e.bar > bar); cur.qi = 0;
    cur.start = now + 0.1 - (bar + 1) * cur.barDur;
  }
  /** Keeping time (the radio): what should have sounded before now is dropped, never shifted, whether the track was
   *  just tuned in mid-way or the main thread froze. The mixer automation lands at once (the last value of each kind),
   *  and the chord still ringing is struck again for what is left of it, so a listener who tunes in hears the harmony. */
  function catchUp(track, now) {
    const fx = new Map();
    let ringing = null;
    while (track.qi < track.q.length && track.start + track.q[track.qi].t < now) {
      const e = track.q[track.qi++];
      if (e.k === 'fx') fx.set(e.fx, e);
      else if (e.k === 'ep' && track.start + e.t + e.dur > now + 0.3) ringing = e;
    }
    for (const e of fx.values()) dispatch(track, e);          // fx lands at max(its time, now)
    if (ringing) {
      const at = now + 0.02, left = track.start + ringing.t + ringing.dur - at;
      track.ep.chord(at, ringing.notes, ringing.vel * 0.85, left, ringing.strum, ringing.rel);
      if (trace) trace({ ...ringing, again: true }, at);
    }
  }
  function notify(now) {
    if (!pending.length) return;
    const ready = pending.filter(p => !p.dead && now >= p.start - 0.03);
    pending = pending.filter(p => !p.dead && now < p.start - 0.03);
    for (const p of ready) { p.notified = true; onTrack(p.plan); }
  }

  function advance(until, depth = 0) {
    if (!cur || disposed) return;
    const now = ac.currentTime;
    runJobs(now);
    if (now - lastSweep > 1) { lastSweep = now; registry.sweep(now); }
    const head = cur.q[cur.qi];
    if (!follow && head && cur.start + head.t < now - 0.05) reanchor(now, head.bar);
    while (cur.nextBar < cur.plan.bars && cur.start + cur.nextBar * cur.barDur < until + LOOK) planNext(cur);
    if (follow) catchUp(cur, now);
    while (cur.qi < cur.q.length && cur.start + cur.q[cur.qi].t < until) dispatch(cur, cur.q[cur.qi++]);
    if (cur.qi > 256) { cur.q = cur.q.slice(cur.qi); cur.qi = 0; }
    if (cur.nextBar >= cur.plan.bars && cur.qi >= cur.q.length && depth < 2) {      // natural handoff
      const end = cur.start + cur.plan.bars * cur.barDur, next = follow ? follow(cur.plan) : null;
      const nextStart = next ? next.start : end + HANDOFF_BARS * cur.barDur;
      if (nextStart < until + LOOK) {
        refill();
        const plan = next ? next.plan : queue.shift();
        retire(cur, end + 5);
        cur = build(plan, nextStart);
        refill();
        pending.push(cur);
        advance(until, depth + 1);
        return;
      }
    }
    notify(now);
  }

  return {
    start(seed, t = ac.currentTime + 0.1) {
      if (cur) retire(cur, ac.currentTime + 0.5);
      cur = build(planTrack(seed, { scene: curScene, prevKey: null, vibe: curVibe }), t);
      queue = []; refill();
      pending.push(cur);
    },
    /**
     * user skip: fade the track (τ 0.1 s), dip the tone LP, stop scheduled voices, next intro at +0.6 s.
     * With a seed, jump to that queued plan (the ones before it are dropped); an unknown seed does nothing.
     */
    next(seed) {
      if (!cur || disposed) return;
      refill();
      const k = seed == null ? 0 : queue.findIndex(p => p.seed === seed);
      if (k < 0) return;
      const now = ac.currentTime, old = cur;
      skips++;
      fadeOut(old.dry.gain, now, 0.1); fadeOut(old.wet.gain, now, 0.1);
      registry.stopTag(old.tag, now, 0.03);
      old.dead = true;
      retire(old, now + 1);
      const tf = mixer.tone.frequency;
      hold(tf, now); tf.setTargetAtTime(400, now, 0.13);
      hold(mixer.vinyl.gain, now); mixer.vinyl.gain.setTargetAtTime(VINYL_BOOST, now, 0.3);
      hold(mixer.tape.wow, now);
      const plan = queue[k];
      queue = queue.slice(k + 1);
      cur = build(plan, now + 0.6);
      refill();
      pending = pending.filter(p => !p.dead);
      pending.push(cur);
    },
    /** The radio: play `plan` as if it began at ac time `t0` (now, later, or already past: then it joins mid-way, see
     *  catchUp). The track playing fades out fast (τ 0.1 s), like a skip; call it with follow() set. */
    tune(plan, t0) {
      if (disposed) return;
      const now = ac.currentTime;
      if (cur) {
        const old = cur;
        fadeOut(old.dry.gain, now, 0.1); fadeOut(old.wet.gain, now, 0.1);
        registry.stopTag(old.tag, now, 0.03);
        old.dead = true;
        retire(old, now + 1);
      }
      cur = build(plan, t0);
      queue = []; refill();
      pending = pending.filter(p => !p.dead);
      pending.push(cur);
    },
    /** fn(plan) → { plan, start } | null: keep time and take the next track from fn (the radio), or null to go back to
     *  the queue, which follows on from the track playing (re-planned, so it chains from it). */
    follow(fn) {
      follow = typeof fn === 'function' ? fn : null;
      if (cur) { queue = []; refill(); }
    },
    /** The radio: move the current track by `d` s from its next unscheduled note on (a few ms to set it where the clock
     *  says; what's already scheduled plays as it is, and notes moved into the past are dropped, see catchUp). */
    nudge(d) { if (cur && Number.isFinite(d)) cur.start += d; },
    /** ac time of the current track's bar 0 (its planned start, or its tuned-in one), null before the first */
    get startTime() { return cur ? cur.start : null; },
    /** how many tracks were built so far: another number is another track, with its own grid (layers.js) */
    get trackNo() { return trackN; },
    advance,
    /** the key sounding now, or at ac time `at` (past a lift, the lifted one): the ambience snaps its pitched one-shots
     *  to it, and a tapped spot's sound (player.tap) is pitched from the key on the beat it lands on */
    key: (at = ac.currentTime) => {
      if (!cur) return null;
      const b = cur.st.map[clamp(Math.floor((at - cur.start) / cur.barDur), 0, cur.st.map.length - 1)];
      return b && b.lift ? liftKey(cur.plan.key, b.lift) : cur.plan.key;
    },
    /** the key and the chord a note landing at ac time `at` sounds over (harmonyAt), null before the first track */
    harmony: (at = ac.currentTime) => (cur ? harmonyAt(cur.plan, cur.st, (at - cur.start) / cur.sd) : null),
    get plan() { return cur ? cur.plan : null; },
    get queue() { return queue.slice(); },
    setScene(sc) {
      curScene = sc;
      if (cur) { queue = []; refill(); }             // same seeds and keys, titles from the new city
      if (cur && !cur.notified && !cur.plan.radio) {   // built but not started yet: only the title/city change (the radio's
                                                       // titles are everyone's, whichever city they watch)
        const p = planTrack(cur.plan.seed, { scene: sc, prevKey: null });
        cur.plan = { ...cur.plan, title: p.title, city: p.city, sceneId: p.sceneId };
      }
    },
    /** Energy and style apply from the next track: the queue is re-planned (same seeds, keys and titles). The band also
     *  reaches the current track, from its first bar that isn't planned yet. */
    setVibe(v) {
      curVibe = normVibe(v, curVibe);
      if (!cur) return;
      const { band } = curVibe;
      if (band !== cur.plan.vibe.band) {
        cur.plan = { ...cur.plan, vibe: { ...cur.plan.vibe, band } };
        const lift = styleOf(cur.plan).keysLevel;
        for (const b of cur.st.map.slice(cur.nextBar)) b.L = bandLayers(cur.plan.sections[b.si].layers, band, lift);
        // no bar planned yet (just built): later bars only ramp on a change, so the bus has to start at the new level
        if (cur.nextBar === 0) cur.dry.gain.value = cur.wet.gain.value = TRACK_GAIN * cur.st.map[0].L.level;
      }
      queue = []; refill();
    },
    stats() {
      const now = ac.currentTime;
      return {
        title: cur && cur.plan.title, seed: cur && cur.plan.seed, vibe: cur && cur.plan.vibe, radio: cur ? cur.plan.radio || null : null,
        bar: cur ? Math.floor((now - cur.start) / cur.barDur) : null, bars: cur && cur.plan.bars, start: cur ? +cur.start.toFixed(4) : null,
        queued: cur ? cur.q.length - cur.qi : 0, reanchors, skips, tracks: trackN, played: { ...played },
        busGain: cur ? +cur.dry.gain.value.toFixed(3) : null,
        ep: cur ? cur.ep.stats() : null, liveVoices: registry.live, nodes: registry.nodes,
      };
    },
    get reanchors() { return reanchors; },
    dispose() {
      if (disposed) return;
      const now = ac.currentTime;
      if (cur) { fadeOut(cur.dry.gain, now, 0.05); fadeOut(cur.wet.gain, now, 0.05); registry.stopTag(cur.tag, now, 0.02); retire(cur, now + 0.4); }
      disposed = true; cur = null; queue = []; pending = [];
      setTimeout(() => runJobs(Infinity), 800);
    },
  };
}
